import React, { useState, useEffect } from 'react';
import { Button, Select } from '@ds';
import { IOption } from '@ds/elements/Select/Select.types';
import ProfileImage from 'src/components/ProfileImage/ProfileImage';
import { useGetDependents } from 'src/shared/queries/useGetDependents';
import { useGetBeneficiary } from 'src/shared/queries/useGetBeneficiary';
import { PlusIcon } from '@ds-icons';
import { useUserProfileStore } from 'src/stores/UserProfile';
import ProfileInitials from 'src/components/ProfileInitials/ProfileInitials';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { Relationship } from 'src/shared/types/Common';
import styles from './ProfileInfo.module.scss';

const ProfileImageDefaultSize = 56;

const ProfileInfo: React.FC = () => {
  const { profileInfo, setProfileInfo, toggleEditMode } = useUserProfileStore();
  const { data: userInfo } = useGetUser();
  const beneId = userInfo?.beneId ?? 0;
  const { data: beneInfo } = useGetBeneficiary(beneId);
  const { data: dependents } = useGetDependents(beneId);
  const [selectedProfile, setSelectedProfile] = useState<IOption | null>(null);

  useEffect(() => {
    if (beneInfo) {
      setProfileInfo({
        id: beneId,
        fullName: beneInfo.fullName,
        relationship: Relationship.Principal,
        isPrincipal: true,
      });
    }
  }, [beneInfo]);

  useEffect(() => {
    if (profileInfo) {
      setSelectedProfile({ label: profileInfo.fullName || '', value: profileInfo.id });
    }
  }, [profileInfo]);

  const onChangeFamilyMember = (option: IOption | null) => {
    const profile = dependents?.find((dependent) => dependent.dependentId === option?.value);
    if (profile) {
      setProfileInfo({
        id: profile.dependentId,
        fullName: profile.fullName,
        firstName: profile.firstName,
        lastName: profile.lastName,
        relationship: profile.relationship,
        isPrincipal: false,
      });
    } else {
      setProfileInfo({
        id: beneId,
        fullName: beneInfo?.fullName ?? '',
        relationship: Relationship.Principal,
        isPrincipal: true,
      });
    }
  };

  const onAddFamilyMember = () => {
    const id = -1;
    const lastName = ((dependents?.length ?? 0) + 1).toString();
    const fullName = `${Relationship.Dependent} ${lastName}`;
    toggleEditMode();
    const obj = {
      id,
      fullName,
      firstName: Relationship.Dependent,
      lastName: lastName,
      relationship: Relationship.Dependent,
      isPrincipal: false,
    };
    setProfileInfo(obj);
  };

  const getMembersList = (
    selectedProfile: IOption | null,
    beneInfo?: { beneId: number; fullName: string },
    dependents?: { dependentId: number; fullName: string }[]
  ) => {
    const members = [];
    if (selectedProfile?.value === -1) members.push(selectedProfile);
    if (beneInfo) members.push({ value: beneInfo.beneId, label: beneInfo.fullName });
    if (dependents?.length)
      dependents.forEach((dependent) => {
        members.push({ value: dependent.dependentId, label: dependent.fullName });
      });
    return members;
  };

  return (
    <div className={styles['profile-info-container']}>
      <div className={styles['profile-info']}>
        {!selectedProfile?.value || selectedProfile.value == userInfo?.beneId ? (
          <ProfileImage size={ProfileImageDefaultSize} />
        ) : (
          <ProfileInitials
            size={ProfileImageDefaultSize}
            firstName={profileInfo?.firstName}
            lastName={profileInfo?.lastName}
          />
        )}
        <div className={'flex flex-col justify-around'}>
          <h3>{profileInfo?.fullName || userInfo?.fullName}</h3>
          <span>{profileInfo?.relationship || Relationship.Principal}</span>
        </div>
      </div>
      <div className={styles['family-members']}>
        <Select
          className={styles['family-members-dropdown']}
          options={getMembersList(selectedProfile, beneInfo, dependents)}
          selectedOption={selectedProfile}
          label="View Profile For"
          searchable={false}
          showClear={false}
          disabled={selectedProfile?.value === -1}
          onChange={onChangeFamilyMember}
        />
        <Button
          size="large"
          disabled={profileInfo?.id === -1}
          className={styles['add-family-members-button']}
          onClick={onAddFamilyMember}
        >
          <img src={PlusIcon} />
          <span>Add family members</span>
        </Button>
      </div>
    </div>
  );
};

export default ProfileInfo;
